import { useEffect, useState, lazy, Suspense } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import Container from "./components/Container";
import Header from "./components/Home/Header";
import Footer from "./components/Home/Footer";
import { HelmetProvider } from "react-helmet-async";
import Canonical from "./components/Canonical";
import { ToastContainer } from "react-toastify";
import { getGuestUserId, injectClarityScript } from "./utils/common";
import india from "./assets/images/india.png";
import uk from "./assets/images/uk.png";

// Lazy load route components
const HomePage = lazy(() => import("./components/Home/HomeIndex"));
const ShopScreens = lazy(() => import("./components/Shop/ShopScreens"));
const ProductPage = lazy(() => import("./components/ShopDetail/productPage"));
const BlogPage = lazy(() => import("./components/Blog/blogPage"));
const Blog = lazy(() => import("./components/Blog/blog"));
const PersonaIndex = lazy(() => import("./components/Persona/PersonaIndex"));
const DetailIndex = lazy(() => import("./components/ReceipeDetail/DetailIndex"));
const ProfilePage = lazy(() => import("./components/Profile/profileIndex"));
const Aboutus = lazy(() => import("./components/AboutUs/Aboutus"));
const PrivacyPolicy = lazy(() => import("./components/CompanyFooter/PrivacyPolicy"));
const OurStoryTwo = lazy(() => import("./components/OurStory/ourstorytwo"));
const ContactUs = lazy(() => import("./components/OurStory/ContactUs"));
const BlogPost = lazy(() => import("./components/Blog/blogPost"));
const ShopExplore = lazy(() => import("./components/Shop/shopExplore"));
const PricingPlans = lazy(() => import("./components/Subscription/Subscription"));
const DeliveryDetails = lazy(() => import("./components/Profile/Order/orderDetails"));
const RefundScreens = lazy(() => import("./components/Profile/Order/orderCancellation"));
const ConfirmCancelPopup = lazy(() => import("./components/Profile/Order/orderCancelledConfirm"));
const OrderConfirmation = lazy(() => import("./components/CheckOut/OrderConfirmation"));
const ShippingPolicy = lazy(() => import("./components/CompanyFooter/ShippingPolicy"));
const CancellationRefunds = lazy(() => import("./components/CompanyFooter/CancellationRefunds"));
const TermsConditions = lazy(() => import("./components/CompanyFooter/TermsConditions"));
const AI = lazy(() => import("./components/CompanyFooter/AI"));
const VendorExplore = lazy(() => import("./components/Vendor/VendorExplore"));
const RecipeCuisine = lazy(() => import("./components/ReceipeDetail/RecipeCuisine"));
const ShopCuisineRedirect = lazy(() => import("./components/Shop/ShopCuisineRedirect"));
const CartPage = lazy(() => import("./components/ReceipeDetail/Cart_2/CartRework"));
const CartCheckoutPage = lazy(() => import("./components/ReceipeDetail/Cart_2/CartCheckout"));
const ChristmasSpecial = lazy(() => import("./components/Home/ChristmasSpecial"));
const IngredientShop = lazy(() => import("./components/Shop/shopExplore"));
const ErrorPage = lazy(() => import("./components/Home/404ErrorPage"));

interface Country {
  name: string;
  flag: string;
}

const AppContainer = () => {
  const countries: Country[] = [
    { name: "UK", flag: uk },
    { name: "India", flag: india },
  ];

  const staticFiles = new Set(["/.well-known/assetlinks.json"]);
  const storedCountry = localStorage.getItem("selectedCountry");
  const storedCode = localStorage.getItem("countryCode");

  const [selectedCountry, setSelectedCountry] = useState<Country | null>(countries[0]);
  const [countryCode, setCountryCode] = useState<string | null>();
  const [guestId, setGuestId] = useState<string>("");

  const location = useLocation();
  const userId = localStorage.getItem("userId");

  useEffect(() => {
    if (userId === null) {
      const newGuestId = getGuestUserId();
      setGuestId(newGuestId);
      localStorage.setItem("guestId", newGuestId);
    } else {
      setGuestId(userId);
      localStorage.setItem("guestId", userId);
    }
  }, [userId]);

  useEffect(() => {
    const fetchUserCountry = async () => {
      try {
        const response = await fetch("https://ipinfo.io?token=2aadf73cb3df29");
        const data = await response.json();
        const userCountry = data.country;

        if (userCountry === "IN") {
          setSelectedCountry(countries.find(c => c.name === "India") || null);
          setCountryCode("IN");
          localStorage.setItem("selectedCountry", "India");
          localStorage.setItem("countryCode", "IN");
        } else {
          setSelectedCountry(countries.find(c => c.name === "UK") || null);
          setCountryCode("UK");
          localStorage.setItem("selectedCountry", "UK");
          localStorage.setItem("countryCode", "UK");
        }
      } catch (error) {
        console.error("Error fetching IP location:", error);
      }
    };

    if (storedCountry && storedCode) {
      const country = countries.find(c => c.name === storedCountry);
      if (country) {
        setSelectedCountry(country);
        setCountryCode(storedCode);
      }
    } else {
      fetchUserCountry();
    }
  }, [storedCountry, storedCode]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    injectClarityScript();
  }, []);

  useEffect(() => {
    if (window._hsq) {
      window._hsq.push(["setPath", location.pathname]);
      window._hsq.push(["trackPageView"]);
    }
  }, [location]);

  return (
    <Container>
      <ToastContainer />
      {!staticFiles.has(location.pathname) && !location.pathname.endsWith("/checkout") && <Header />}

      <HelmetProvider>
        <Canonical />

        {location.pathname === "/" ? (
          <HomePage />
        ) : (
          <Suspense fallback={<div>Loading...</div>}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/:countryCode" element={<HomePage />} />
              <Route path="/:countryCode/shop" element={<ShopScreens />} />
              <Route path="/:countryCode/shop/cuisine" element={<ShopCuisineRedirect />} />
              <Route path="/:countryCode/shop/cuisine/:cuisine" element={<ShopExplore />} />
              <Route path="/:countryCode/shop/cuisine/:cuisine/:category/:sub_category/:url" element={<ProductPage />} />
              <Route path="/:countryCode/viewall" element={<IngredientShop />} />
              <Route path="/:countryCode/recipe/:consumer/:url" element={<DetailIndex />} />
              <Route path="/:countryCode/recipe/cuisine/:cuisine" element={<RecipeCuisine />} />
              <Route path="/:countryCode/recipe/cuisine/:cuisine/:consumer/:url" element={<DetailIndex />} />
              <Route path="/:countryCode/recipe/cuisine/:cuisine/persona/:id" element={<PersonaIndex />} />
              <Route path="/:countryCode/persona/:id" element={<PersonaIndex />} />
              <Route path="/:countryCode/profile" element={<ProfilePage />} />
              <Route path="/:countryCode/cart" element={<CartPage />} />
              <Route path="/:countryCode/checkout" element={<CartCheckoutPage />} />
              <Route path="/:countryCode/blog" element={<BlogPage />} />
              <Route path="/:countryCode/blog/:title" element={<Blog />} />
              <Route path="/:countryCode/contact" element={<ContactUs />} />
              <Route path="/:countryCode/PrivacyPolicy" element={<PrivacyPolicy />} />
              <Route path="/:countryCode/ShippingPolicy" element={<ShippingPolicy />} />
              <Route path="/:countryCode/CancellationRefunds" element={<CancellationRefunds />} />
              <Route path="/:countryCode/TermsConditions" element={<TermsConditions />} />
              <Route path="/:countryCode/OurStory" element={<OurStoryTwo />} />
              <Route path="/:countryCode/AI" element={<AI />} />
              <Route path="/:countryCode/vendor/:city/:z1/:z2/:url" element={<VendorExplore />} />
              <Route path="/:countryCode/subscription" element={<PricingPlans />} />
              <Route path="/:countryCode/order/:id" element={<DeliveryDetails />} />
              <Route path="/:countryCode/order-cancel/:id" element={<RefundScreens />} />
              <Route path="/:countryCode/OrderCancelled/:id" element={<ConfirmCancelPopup />} />
              <Route path="/:countryCode/OrderConfirm/:id" element={<OrderConfirmation />} />
              <Route path="/:countryCode/holi-special" element={<ChristmasSpecial />} />
              <Route path="/:countryCode/holi-festive" element={<ChristmasSpecial />} />
              <Route path="/:countryCode/404" element={<ErrorPage />} />
              <Route path="*" element={<ErrorPage />} />
            </Routes>
          </Suspense>
        )}
      </HelmetProvider>

      {!staticFiles.has(location.pathname) && <Footer />}
    </Container>
  );
};

export default AppContainer;
